import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

interface CardProps {
  children: ReactNode;
  className?: string;
  variant?: 'default' | 'stats' | 'accent';
}

/**
 * Sdílená karta — Plex glass surface (LHUI).
 * variant:
 *   default — glass panel
 *   stats   — hustší glass (metriky)
 *   accent  — jemný plex glow / gradient
 */
export function Card({ children, className, variant = 'default' }: CardProps) {
  const variants = {
    default: 'acm-glass acm-glass-edge',
    stats: 'acm-glass-strong acm-glass-edge',
    accent: 'acm-glass-accent',
  };

  return (
    <div className={cn('rounded-2xl overflow-hidden', variants[variant], className)}>
      {children}
    </div>
  );
}

interface CardHeaderProps {
  children: ReactNode;
  className?: string;
}

export function CardHeader({ children, className }: CardHeaderProps) {
  return (
    <div
      className={cn(
        'px-5 py-2.5 border-b border-gray-700/50 flex items-center justify-between',
        'bg-gray-950/30',
        className,
      )}
    >
      {children}
    </div>
  );
}

interface CardTitleProps {
  children: ReactNode;
  className?: string;
  icon?: React.ComponentType<{ className?: string; size?: number | string }>;
}

export function CardTitle({ children, className, icon: Icon }: CardTitleProps) {
  return (
    <h3 className={cn('text-sm font-semibold text-gray-100 flex items-center gap-2', className)}>
      {Icon && <Icon size={16} className='text-plex' />}
      {children}
    </h3>
  );
}

interface CardDescriptionProps {
  children: ReactNode;
  className?: string;
}

export function CardDescription({ children, className }: CardDescriptionProps) {
  return (
    <p className={cn('text-xs text-gray-500 mt-0.5', className)}>
      {children}
    </p>
  );
}

interface CardContentProps {
  children: ReactNode;
  className?: string;
}

export function CardContent({ children, className }: CardContentProps) {
  return (
    <div className={cn('p-5', className)}>
      {children}
    </div>
  );
}

interface CardFooterProps {
  children: ReactNode;
  className?: string;
}

export function CardFooter({ children, className }: CardFooterProps) {
  return (
    <div
      className={cn(
        'px-5 py-3 border-t border-gray-700/50 bg-gray-950/30',
        className,
      )}
    >
      {children}
    </div>
  );
}
